import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Download, Copy, FileText, Clock, CheckCircle, AlertCircle, Loader, Eye, EyeOff, BookOpen, ExternalLink } from 'lucide-react'
import { buttonVariants, componentClasses, cn } from '../../utils/designSystem'
import { formatProgress, estimateTimeRemaining, parseMarkdownContent, formatSourceToAPA, countWords } from './skripsiUtils'
import { CheckCircle2 } from 'lucide-react'
import AgentHeader from '../../components/AgentHeader'
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, NumberFormat, Table, TableRow, TableCell, WidthType, BorderStyle } from 'docx'
import { saveAs } from 'file-saver'

// Component for displaying academic sources as cards
const SourceCard = ({ source, index }) => {
  // Extract domain from URL for favicon
  const getDomainFromUrl = (url) => {
    try {
      const domain = new URL(url).hostname
      return domain.replace('www.', '')
    } catch {
      return 'unknown'
    }
  }

  const domain = getDomainFromUrl(source.url)
  const faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=32`

  return (
    <div className="bg-bg-tertiary rounded-lg p-3 sm:p-4 border border-border-secondary hover:border-border-primary transition-colors h-32 flex flex-col">
      <div className="flex items-start space-x-2 sm:space-x-3 flex-1">
        <div className="flex-shrink-0">
          <img 
            src={faviconUrl}
            alt={`${domain} favicon`}
            className="w-5 h-5 sm:w-6 sm:h-6 rounded"
            onError={(e) => {
              e.target.style.display = 'none'
              e.target.nextSibling.style.display = 'block'
            }}
          />
          <div className="w-5 h-5 sm:w-6 sm:h-6 bg-emerald-500/10 rounded flex items-center justify-center" style={{display: 'none'}}>
            <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500" />
          </div>
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="text-xs sm:text-sm font-medium text-text-primary mb-1">
            {domain}
          </div>
          <div className="text-sm font-semibold text-text-primary leading-tight flex-1" style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {source.title}
          </div>
        </div>
      </div>
      <div className="mt-2 pt-2 border-t border-border-secondary">
        <a 
          href={source.url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
        >
          <ExternalLink className="w-3 h-3 mr-1" />
          <span className="hidden sm:inline">Buka Sumber</span>
          <span className="sm:hidden">Buka</span>
        </a>
      </div>
    </div>
  )
}

// Component for displaying APA references as cards
const ReferenceCard = ({ reference, index }) => {
  return (
    <div className="bg-bg-tertiary rounded-lg p-3 sm:p-4 border border-border-secondary hover:border-border-primary transition-colors h-full">
      <div className="flex items-start space-x-2 sm:space-x-3 h-full">
        <div className="p-1.5 sm:p-2 bg-blue-500/10 rounded-lg flex-shrink-0">
          <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500" />
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="text-xs sm:text-sm font-medium text-text-primary mb-1">
            Referensi {index + 1}
          </div>
          <div className="text-xs sm:text-sm text-text-secondary leading-relaxed break-words overflow-hidden flex-1">
            {reference}
          </div>
          {reference.includes('http') && (
            <div className="mt-2 pt-2">
              <a 
                href={reference.match(/https?:\/\/[^\s)]+/)?.[0]} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center text-xs text-blue-400 hover:text-blue-300 transition-colors"
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                <span className="hidden sm:inline">Buka Link</span>
                <span className="sm:hidden">Link</span>
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Component for rendering parsed markdown content without citations
const MarkdownContent = ({ content }) => {
  const { parsedContent } = parseMarkdownContent(content) || { parsedContent: '' }
  
  // Clean content to remove references and bibliographies that might appear at the end of chapters
  const cleanContent = parsedContent
    // Remove various reference section patterns
    .replace(/\n\s*##?\s*(Referensi|Daftar Pustaka|References|Bibliography)\s*\n[\s\S]*$/gi, '')
    .replace(/\n\s*\*\*\s*(Referensi|Daftar Pustaka|References|Bibliography)\s*\*\*\s*\n[\s\S]*$/gi, '')
    // Remove numbered reference lists at the end
    .replace(/\n\s*\d+\.\s+[^\n]*\([12]\d{3}\)[\s\S]*$/g, '')
    // Remove author-year citation patterns
    .replace(/\([A-Za-z]+,?\s*[12]\d{3}\)/g, '')
    // Remove bracketed citations
    .replace(/\[\d+\]/g, '')
    .trim()
  
  const wordCount = countWords(cleanContent)
  
  return (
    <div className="space-y-6">
      {/* Word count removed from UI */}
      
      {/* Main Content */}
      <div className="prose prose-invert max-w-none text-text-secondary leading-relaxed">
        <ReactMarkdown 
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({node, ...props}) => <h1 className="text-xl sm:text-2xl font-bold text-text-primary mb-3 sm:mb-4 mt-4 sm:mt-6" {...props} />,
            h2: ({node, ...props}) => <h2 className="text-lg sm:text-xl font-semibold text-text-primary mb-2 sm:mb-3 mt-4 sm:mt-5" {...props} />,
            h3: ({node, ...props}) => <h3 className="text-base sm:text-lg font-medium text-text-primary mb-2 mt-3 sm:mt-4" {...props} />,
            h4: ({node, ...props}) => <h4 className="text-sm sm:text-base font-medium text-text-primary mb-2 mt-2 sm:mt-3" {...props} />,
            p: ({node, ...props}) => <p className="mb-3 sm:mb-4 text-text-secondary leading-relaxed text-sm sm:text-base" {...props} />,
            ul: ({node, ...props}) => <ul className="list-disc ml-4 sm:ml-6 mb-3 sm:mb-4 space-y-1 sm:space-y-2 text-text-secondary text-sm sm:text-base" {...props} />,
            ol: ({node, ...props}) => <ol className="list-decimal ml-4 sm:ml-6 mb-3 sm:mb-4 space-y-1 sm:space-y-2 text-text-secondary text-sm sm:text-base" {...props} />,
            li: ({node, ...props}) => <li className="mb-1 leading-relaxed" {...props} />,
            blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-emerald-500 pl-3 sm:pl-4 italic text-text-tertiary mb-3 sm:mb-4 text-sm sm:text-base" {...props} />,
            code: ({node, inline, ...props}) => 
              inline 
                ? <code className="bg-bg-tertiary text-emerald-400 px-1 py-0.5 rounded text-xs sm:text-sm" {...props} />
                : <code className="block bg-bg-tertiary text-text-secondary p-2 sm:p-4 rounded-lg mb-3 sm:mb-4 overflow-x-auto text-xs sm:text-sm" {...props} />,
            strong: ({node, ...props}) => <strong className="font-semibold text-text-primary" {...props} />,
            em: ({node, ...props}) => <em className="italic text-text-secondary" {...props} />,
            a: ({node, ...props}) => <a className="text-emerald-400 hover:text-emerald-300 underline break-words" {...props} />,
            table: ({node, ...props}) => (
              <div className="overflow-x-auto mb-4 sm:mb-6">
                <table className="w-full border-collapse border border-border-secondary rounded-lg text-xs sm:text-sm" {...props} />
              </div>
            ),
            th: ({node, ...props}) => <th className="border border-border-secondary bg-bg-tertiary p-2 sm:p-3 text-left font-semibold text-text-primary" {...props} />,
            td: ({node, ...props}) => <td className="border border-border-secondary p-2 sm:p-3 text-text-secondary" {...props} />
          }}
        >
          {cleanContent}
        </ReactMarkdown>
      </div>
    </div>
  )
}

const SkripsiResultPage = ({ 
  query, 
  outline, 
  generatedContent, 
  academicSources = [], 
  progress, 
  isLoading, 
  error, 
  isCompleted, 
  onBack, 
  onBackToSearch,
  isSaving = false,
  lastSaveTime = null 
}) => {
  const [startTime] = useState(Date.now())
  const [copyStatus, setCopyStatus] = useState('idle')
  const [expandedSections, setExpandedSections] = useState(new Set())
  const [showAllContent, setShowAllContent] = useState(false)

  // Auto scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const progressPercentage = formatProgress(progress.current, progress.total)
  const timeRemaining = estimateTimeRemaining(progress.current, progress.total, startTime)
  
  // Calculate word statistics
  const calculateWordStats = () => {
    let totalWords = 0
    const sectionStats = []
    
    outline.forEach(section => {
      const content = generatedContent[section.id]
      const wordCount = content ? countWords(content) : 0
      
      totalWords += wordCount
      
      if (wordCount > 0) {
        sectionStats.push({
          title: section.title,
          current: wordCount
        })
      }
    })
    
    return {
      totalWords,
      sectionStats
    }
  }
  
  const wordStats = calculateWordStats()

  // Collect all citations from all sections
  const collectAllCitations = () => {
    const allCitations = []
    outline.forEach(section => {
      const content = generatedContent[section.id]
      if (content) {
        const { citations } = parseMarkdownContent(content) || { citations: [] }
        allCitations.push(...citations)
      }
    })
    // Remove duplicates and return unique citations
    return [...new Set(allCitations)]
  }

  const allCitations = collectAllCitations()

  // Convert markdown to structured text that preserves formatting for Word/Google Docs
  const convertMarkdownToText = (markdown) => {
    if (!markdown) return ''
    
    return markdown
      // Remove references and bibliographies first
      .replace(/\n\s*##?\s*(Referensi|Daftar Pustaka|References|Bibliography)\s*\n[\s\S]*$/gi, '')
      .replace(/\n\s*\*\*\s*(Referensi|Daftar Pustaka|References|Bibliography)\s*\*\*\s*\n[\s\S]*$/gi, '')
      .replace(/\n\s*\d+\.\s+[^\n]*\([12]\d{3}\)[\s\S]*$/g, '')
      .replace(/\([A-Za-z]+,?\s*[12]\d{3}\)/g, '')
      .replace(/\[\d+\]/g, '')
      // Convert headers to proper formatting
      .replace(/^#{1}\s+(.+)$/gm, '$1\n') // H1
      .replace(/^#{2}\s+(.+)$/gm, '$1\n') // H2
      .replace(/^#{3}\s+(.+)$/gm, '$1\n') // H3
      .replace(/^#{4}\s+(.+)$/gm, '$1\n') // H4
      .replace(/^#{5}\s+(.+)$/gm, '$1\n') // H5
      .replace(/^#{6}\s+(.+)$/gm, '$1\n') // H6
      // Preserve bold and italic formatting
      .replace(/\*\*(.*?)\*\*/g, '$1') // Keep bold text content
      .replace(/\*(.*?)\*/g, '$1') // Keep italic text content
      // Handle code blocks and inline code
      .replace(/`(.*?)`/g, '$1') // Remove inline code backticks
      .replace(/```[\s\S]*?```/g, '') // Remove code blocks
      // Convert links to text with URL
      .replace(/\[(.*?)\]\((.*?)\)/g, '$1 ($2)') // Convert links to "text (url)"
      // Handle lists properly
      .replace(/^[-*+]\s+/gm, '• ') // Convert unordered lists to bullets
      .replace(/^(\d+)\.\s+/gm, '$1. ') // Keep numbered lists
      // Handle tables - convert to simple format
      .replace(/\|\s*(.+?)\s*\|/g, '$1') // Remove table pipes, keep content
      .replace(/^\s*[-=|:\s]+\s*$/gm, '') // Remove table separators
      // Remove horizontal rules
      .replace(/^\s*[-=]{3,}\s*$/gm, '') // Remove horizontal rules
      // Normalize line breaks
      .replace(/\n{3,}/g, '\n\n') // Normalize multiple line breaks
      .trim()
  }

  const handleCopyAll = async () => {
    try {
      setCopyStatus('copying')
      
      let fullContent = `SKRIPSI\n\nTopik: ${query}\n\n`
      
      // Process outline sections (BAB I-V, Lampiran, Daftar Pustaka)
      outline.forEach(section => {
        const content = generatedContent[section.id]
        if (content) {
          const structuredContent = convertMarkdownToText(content)
          fullContent += `${section.title}\n${'='.repeat(section.title.length)}\n\n${structuredContent}\n\n\n`
        }
      })
      
      // Add Daftar Pustaka if citations exist
      if (allCitations.length > 0) {
        fullContent += `DAFTAR PUSTAKA\n${'='.repeat('DAFTAR PUSTAKA'.length)}\n\n`
        allCitations.forEach((citation, index) => {
          fullContent += `${index + 1}. ${citation}\n\n`
        })
      }
      
      await navigator.clipboard.writeText(fullContent)
      setCopyStatus('copied')
      setTimeout(() => setCopyStatus('idle'), 2000)
    } catch (error) {
      console.error('Error copying content:', error)
      setCopyStatus('idle')
    }
  }

  // Convert markdown to Word document paragraphs with rich formatting
  const convertMarkdownToWordParagraphs = (markdown) => {
    if (!markdown) return []
    
    const paragraphs = []
    const lines = markdown.split('\n')
    let currentParagraph = []
    let inTable = false
    let tableRows = []
    
    const standardFontSize = 24 // 12pt in half-points
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim()
      
      // Improved table detection - check for proper markdown table format
      if (line.includes('|') && !inTable && line.split('|').length >= 3) {
        // Start of table - finish current paragraph first
        if (currentParagraph.length > 0) {
          paragraphs.push(new Paragraph({
            children: currentParagraph,
            spacing: { after: 200 }
          }))
          currentParagraph = []
        }
        inTable = true
        tableRows = []
        
        // Process the first table row immediately
        const cells = line.split('|')
          .map(cell => cell.trim())
          .filter((cell, index, arr) => {
            // Remove empty cells at start and end (common in markdown tables)
            return !(cell === '' && (index === 0 || index === arr.length - 1))
          })
        
        if (cells.length > 0) {
          tableRows.push(cells)
        }
        continue
      }
      
      // Handle table content
      if (inTable && line.includes('|')) {
        // Skip separator rows (like |---|---|)
        if (line.match(/^\s*\|?\s*[-:]+\s*(\|\s*[-:]+\s*)*\|?\s*$/)) {
          continue
        }
        
        const cells = line.split('|')
          .map(cell => cell.trim())
          .filter((cell, index, arr) => {
            // Remove empty cells at start and end
            return !(cell === '' && (index === 0 || index === arr.length - 1))
          })
        
        if (cells.length > 0) {
          tableRows.push(cells)
        }
        continue
      }
      
      // End of table
      if (inTable && (!line.includes('|') || line === '')) {
        if (tableRows.length > 0) {
          paragraphs.push(createTable(tableRows))
        }
        inTable = false
        tableRows = []
        
        // If this is an empty line, continue to next iteration
        if (line === '') {
          continue
        }
      }
      
      // Skip empty lines but add paragraph break
      if (!line) {
        if (currentParagraph.length > 0) {
          paragraphs.push(new Paragraph({
            children: currentParagraph,
            spacing: { after: 200 }
          }))
          currentParagraph = []
        }
        continue
      }
      
      // Handle headers
      if (line.startsWith('#')) {
        // Finish current paragraph first
        if (currentParagraph.length > 0) {
          paragraphs.push(new Paragraph({
            children: currentParagraph,
            spacing: { after: 200 }
          }))
          currentParagraph = []
        }
        
        const headerLevel = line.match(/^#+/)[0].length
        const headerText = line.replace(/^#+\s*/, '')
        
        let heading = HeadingLevel.HEADING_1
        let fontSize = 28 // 14pt in half-points
        
        switch (headerLevel) {
          case 1:
            heading = HeadingLevel.HEADING_1
            fontSize = 28 // 14pt
            break
          case 2:
            heading = HeadingLevel.HEADING_2
            fontSize = 26 // 13pt
            break
          case 3:
            heading = HeadingLevel.HEADING_3
            fontSize = 24 // 12pt
            break
          default:
            heading = HeadingLevel.HEADING_4
            fontSize = 22 // 11pt
        }
        
        // Check if this is a main chapter heading (BAB)
        const isMainChapter = headerText.toUpperCase().includes('BAB')
        
        paragraphs.push(new Paragraph({
          children: [new TextRun({ 
            text: headerText, 
            bold: true, 
            size: fontSize,
            color: '000000' // Black color
          })],
          heading: heading,
          spacing: { 
            before: isMainChapter ? 600 : 400, 
            after: 200 
          }
        }))
        continue
      }
      
      // Handle list items
      if (line.match(/^[-*+]\s+/) || line.match(/^\d+\.\s+/)) {
        // Finish current paragraph first
        if (currentParagraph.length > 0) {
          paragraphs.push(new Paragraph({
            children: currentParagraph,
            spacing: { after: 200 }
          }))
          currentParagraph = []
        }
        
        const listText = line.replace(/^[-*+\d.]+\s+/, '')
        const processedText = processInlineFormatting(listText, standardFontSize)
        
        paragraphs.push(new Paragraph({
          children: [new TextRun({ text: '• ', size: standardFontSize, color: '000000' }), ...processedText],
          indent: { left: 720 },
          spacing: { after: 100 }
        }))
        continue
      }
      
      // Regular text - process inline formatting
      const processedText = processInlineFormatting(line, standardFontSize)
      currentParagraph.push(...processedText)
      
      // Add space between sentences
      if (i < lines.length - 1 && lines[i + 1].trim()) {
        currentParagraph.push(new TextRun({ text: ' ', size: standardFontSize, color: '000000' }))
      }
    }
    
    // Handle table at end of content
    if (inTable && tableRows.length > 0) {
      paragraphs.push(createTable(tableRows))
    }
    
    // Add final paragraph if exists
    if (currentParagraph.length > 0) {
      paragraphs.push(new Paragraph({
        children: currentParagraph,
        spacing: { after: 200 }
      }))
    }
    
    return paragraphs
  }
  
  // Create a simple, compatible table with rich text formatting
  const createTable = (tableRows) => {
    if (!tableRows || tableRows.length === 0) return null
    
    const standardFontSize = 24 // 12pt in half-points for better compatibility
    
    // Ensure all rows have the same number of columns
    const maxColumns = Math.max(...tableRows.map(row => row.length))
    const normalizedRows = tableRows.map(row => {
      const normalizedRow = [...row]
      while (normalizedRow.length < maxColumns) {
        normalizedRow.push('')
      }
      return normalizedRow
    })
    
    const rows = normalizedRows.map((rowData, rowIndex) => {
      const cells = rowData.map((cellText, cellIndex) => {
        // Clean cell text and handle empty cells
        const cleanText = (cellText || '').toString().trim()
        
        if (!cleanText) {
          // Handle empty cells
          return new TableCell({
            children: [new Paragraph({
              children: [new TextRun({ 
                text: ' ',
                size: standardFontSize,
                font: 'Times New Roman'
              })],
              alignment: rowIndex === 0 ? AlignmentType.CENTER : AlignmentType.LEFT
            })]
          })
        }
        
        // Process markdown formatting in cell text
        const processedTextRuns = processInlineFormatting(cleanText, standardFontSize)
        
        // Apply header row formatting (bold) if it's the first row
        if (rowIndex === 0) {
          processedTextRuns.forEach(run => {
            if (run.bold === undefined) {
              run.bold = true
            }
          })
        }
        
        return new TableCell({
          children: [new Paragraph({
            children: processedTextRuns,
            alignment: rowIndex === 0 ? AlignmentType.CENTER : AlignmentType.LEFT
          })]
        })
      })
      
      return new TableRow({
        children: cells
      })
    })
    
    return new Table({
      rows: rows,
      width: {
        size: 100,
        type: WidthType.PERCENTAGE
      }
    })
  }
  
  // Process inline formatting (bold, italic, links, etc.)
  const processInlineFormatting = (text, fontSize = 24) => {
    const runs = []
    let currentText = text
    
    // Remove citations first
    currentText = currentText
      .replace(/\([A-Za-z]+,?\s*[12]\d{3}\)/g, '')
      .replace(/\[\d+\]/g, '')
    
    // Process bold text
    const boldRegex = /\*\*(.*?)\*\*/g
    let lastIndex = 0
    let match
    
    while ((match = boldRegex.exec(currentText)) !== null) {
      // Add text before bold
      if (match.index > lastIndex) {
        const beforeText = currentText.substring(lastIndex, match.index)
        if (beforeText) {
          runs.push(...processItalicAndLinks(beforeText, fontSize))
        }
      }
      
      // Add bold text
      runs.push(new TextRun({ 
        text: match[1], 
        bold: true, 
        size: fontSize, 
        color: '000000' 
      }))
      lastIndex = match.index + match[0].length
    }
    
    // Add remaining text
    if (lastIndex < currentText.length) {
      const remainingText = currentText.substring(lastIndex)
      if (remainingText) {
        runs.push(...processItalicAndLinks(remainingText, fontSize))
      }
    }
    
    return runs.length > 0 ? runs : [new TextRun({ 
      text: currentText, 
      size: fontSize, 
      color: '000000' 
    })]
  }
  
  // Process italic and links
  const processItalicAndLinks = (text, fontSize = 24) => {
    const runs = []
    
    // Process links first
    const linkRegex = /\[(.*?)\]\((.*?)\)/g
    let currentText = text.replace(linkRegex, '$1 ($2)')
    
    // Process italic
    const italicRegex = /\*(.*?)\*/g
    let lastIndex = 0
    let match
    
    while ((match = italicRegex.exec(currentText)) !== null) {
      // Add text before italic
      if (match.index > lastIndex) {
        const beforeText = currentText.substring(lastIndex, match.index)
        if (beforeText) {
          runs.push(new TextRun({ 
            text: beforeText, 
            size: fontSize, 
            color: '000000' 
          }))
        }
      }
      
      // Add italic text
      runs.push(new TextRun({ 
        text: match[1], 
        italics: true, 
        size: fontSize, 
        color: '000000' 
      }))
      lastIndex = match.index + match[0].length
    }
    
    // Add remaining text
    if (lastIndex < currentText.length) {
      const remainingText = currentText.substring(lastIndex)
      if (remainingText) {
        runs.push(new TextRun({ 
          text: remainingText, 
          size: fontSize, 
          color: '000000' 
        }))
      }
    }
    
    return runs.length > 0 ? runs : [new TextRun({ 
      text: currentText, 
      size: fontSize, 
      color: '000000' 
    })]
  }

  const handleDownload = async () => {
    try {
      const children = []
      
      // Title page
      children.push(
        new Paragraph({
          children: [new TextRun({ text: 'SKRIPSI', bold: true, size: 32, color: '000000' })],
          alignment: AlignmentType.CENTER,
          spacing: { before: 400, after: 800 }
        }),
        new Paragraph({
          children: [new TextRun({ text: `Topik: ${query}`, size: 24, color: '000000' })],
          alignment: AlignmentType.CENTER,
          spacing: { after: 1200 }
        })
      )
      
      // Process outline sections (BAB I-V, Lampiran, Daftar Pustaka)
      outline.forEach((section, index) => {
        const content = generatedContent[section.id]
        if (content) {
          // Add page break before each section (except first)
          if (index > 0) {
            children.push(
              new Paragraph({
                children: [new TextRun({ text: '', size: 1 })],
                pageBreakBefore: true
              })
            )
          }
          
          // Add section content with rich formatting (heading will be processed from markdown)
          const contentParagraphs = convertMarkdownToWordParagraphs(content)
          children.push(...contentParagraphs)
        }
      })
      
      // Add Daftar Pustaka if citations exist
      if (allCitations.length > 0) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: '', size: 1 })],
            pageBreakBefore: true
          })
        )
        
        allCitations.forEach((citation, index) => {
          children.push(
            new Paragraph({
              children: [new TextRun({ text: `${index + 1}. ${citation}`, size: 24, color: '000000' })],
              spacing: { after: 200 },
              indent: { hanging: 360 }
            })
          )
        })
      }
      
      // Create Word document with maximum compatibility
      const doc = new Document({
        creator: 'Skripsi Generator',
        title: `Skripsi - ${query}`,
        description: 'Generated thesis document',
        styles: {
          default: {
            document: {
              run: {
                size: 24, // 12pt
                font: 'Times New Roman',
                color: '000000'
              },
              paragraph: {
                spacing: {
                  line: 276, // Single line spacing for better compatibility
                  before: 0,
                  after: 120
                }
              }
            }
          }
        },
        sections: [{
          properties: {
            page: {
              margin: {
                top: 1440,    // 1 inch
                right: 1440,  // 1 inch
                bottom: 1440, // 1 inch
                left: 1440    // 1 inch
              }
            }
          },
          children: children
        }]
      })
      
      // Generate and download the document
      const blob = await Packer.toBlob(doc)
      
      const fileName = `skripsi-${query.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}.docx`
      saveAs(blob, fileName)
      
    } catch (error) {
      console.error('Error creating Word document:', error)
      alert('Terjadi kesalahan saat membuat dokumen Word. Silakan coba lagi.')
    }
  }




  const toggleSection = (sectionId) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId)
    } else {
      newExpanded.add(sectionId)
    }
    setExpandedSections(newExpanded)
  }

  const toggleAllContent = () => {
    if (showAllContent) {
      setExpandedSections(new Set())
    } else {
      setExpandedSections(new Set(outline.map(s => s.id)))
    }
    setShowAllContent(!showAllContent)
  }

  return (
    <div className="min-h-screen bg-bg-primary overflow-x-hidden">
      {/* Header */}
      <AgentHeader
        onBack={onBack}
        icon={FileText}
        title="Skripsi Agent"
        query={isCompleted ? 'Outline Detail Selesai' : 'Sedang Membuat Outline Detail...'}
        iconColor="text-emerald-500"
        iconBgColor="bg-emerald-500/10"
        actions={isCompleted ? [
          {
            onClick: toggleAllContent,
            icon: showAllContent ? EyeOff : Eye,
            label: showAllContent ? 'Sembunyikan Semua' : 'Tampilkan Semua',
            hideOnMobile: true,
            className: cn(buttonVariants.secondary, "flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm")
          },
          {
            onClick: handleCopyAll,
            icon: copyStatus === 'copying' ? Loader : copyStatus === 'copied' ? CheckCircle : Copy,
            label: copyStatus === 'copying' ? 'Menyalin...' : copyStatus === 'copied' ? 'Tersalin!' : 'Salin Semua',
            hideOnMobile: true,
            disabled: copyStatus === 'copying',
            className: cn(buttonVariants.secondary, "flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm", copyStatus === 'copying' && "animate-spin")
          },
          {
            onClick: handleDownload,
            icon: Download,
            label: 'Download',
            hideOnMobile: true,
            className: cn(buttonVariants.primary, "flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm")
          }
        ] : []}
      />

      <div className="max-w-6xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
        {/* Topic Display */}
        <div className="bg-bg-secondary rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 border border-border-primary">
          <h2 className="text-base sm:text-lg font-semibold text-text-primary mb-2">Topik Skripsi</h2>
          <p className="text-text-secondary text-base sm:text-lg break-words">"{query}"</p>
          <p className="text-xs sm:text-sm text-text-tertiary mt-2">📋 Outline detail untuk struktur skripsi</p>
        </div>

        {/* Completion Message - Moved to top */}
        {isCompleted && (
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500" />
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-emerald-400">Skripsi Berhasil Dibuat!</h3>
                <p className="text-sm sm:text-base text-emerald-300">
                  Semua {progress.total} bagian telah selesai diproses. Anda dapat melihat, menyalin, atau mengunduh hasilnya.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Word Count Statistics - Moved to top */}
        {isCompleted && Object.keys(generatedContent).length > 0 && (
          <div className="bg-bg-secondary rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 border border-border-primary">
            <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-3 sm:mb-4 flex items-center">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-500" />
              Statistik Jumlah Kata
            </h3>
            
            {/* Total Word Count */}
            <div className="bg-bg-tertiary rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-text-primary">Total Kata</span>
                <span className="text-sm font-semibold text-emerald-400">
                  {wordStats.totalWords.toLocaleString()} kata
                </span>
              </div>
              <div className="text-xs text-text-tertiary">
                📝 Jumlah kata keseluruhan dalam skripsi
              </div>
            </div>
             
             {/* Section-wise breakdown */}
             {wordStats.sectionStats.length > 0 && (
               <div className="space-y-3">
                 <h4 className="text-sm font-medium text-text-primary mb-3">Detail per Bagian:</h4>
                 {wordStats.sectionStats.map((stat, index) => (
                   <div key={index} className="rounded-lg p-3 bg-bg-tertiary">
                     <div className="flex items-center justify-between mb-2">
                       <span className="text-sm text-text-primary font-medium truncate">
                         {stat.title}
                       </span>
                       <span className="text-xs font-semibold text-emerald-400">
                         {stat.current.toLocaleString()} kata
                       </span>
                     </div>
                   </div>
                 ))}
               </div>
             )}
          </div>
        )}





        {/* Progress Section */}
        {!isCompleted && (
          <div className="bg-bg-secondary rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 border border-border-primary">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-text-primary">Progress Outline Detail</h3>
              <div className="text-right">
                <div className="text-xs sm:text-sm font-medium text-text-primary">
                  {progress.current} / {progress.total} bagian
                </div>
                <div className="text-xs text-text-tertiary">
                  {progressPercentage}% selesai
                </div>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-bg-tertiary rounded-full h-3 mb-2">
              <div 
                className="bg-emerald-500 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            
            {/* Warning Text */}
            <p className="text-xs text-text-tertiary mb-4">⚠️ Selama proses sedang berjalan jangan tutup tab browser Anda.</p>
            
            {/* Saving Status */}
            {(isSaving || lastSaveTime) && (
              <div className="flex items-center space-x-2 text-xs sm:text-sm mb-3">
                {isSaving ? (
                  <>
                    <Loader className="w-3 h-3 sm:w-4 sm:h-4 animate-spin text-blue-500" />
                    <span className="text-blue-400">Menyimpan progress...</span>
                  </>
                ) : lastSaveTime && (
                  <>
                    <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500" />
                    <span className="text-emerald-400">
                      Tersimpan otomatis {new Date(lastSaveTime).toLocaleTimeString('id-ID', { 
                        hour: '2-digit', 
                        minute: '2-digit',
                        second: '2-digit'
                      })}
                    </span>
                  </>
                )}
              </div>
            )}
            
            {/* Time Estimate */}
            {timeRemaining && (
              <div className="flex items-center space-x-2 text-xs sm:text-sm text-text-tertiary">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>Estimasi waktu tersisa: {timeRemaining}</span>
              </div>
            )}
            
            {/* Current Section */}
            {isLoading && progress.current < progress.total && (
              <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <Loader className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 animate-spin" />
                  <div>
                    <p className="text-sm sm:text-base font-medium text-blue-400">
                      Sedang membuat outline detail: {outline[progress.current]?.title}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Academic Sources Section - Responsive grid */}
        {academicSources && academicSources.length > 0 && (
          <div className="bg-bg-secondary rounded-xl p-6 mb-8 border border-border-primary">
            <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
              <BookOpen className="w-5 h-5 mr-2 text-emerald-500" />
              Sumber Akademik ({academicSources.length})
            </h3>
            <p className="text-sm text-text-tertiary mb-4">
              Sumber-sumber akademik yang digunakan sebagai referensi dalam pembuatan skripsi ini.
            </p>
            
            {/* Responsive grid layout */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {academicSources.map((source, index) => (
                <SourceCard key={index} source={source} index={index} />
              ))}
            </div>
          </div>
        )}



        {/* Error Display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex items-start space-x-2 sm:space-x-3">
              <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-base sm:text-lg font-medium text-red-400 mb-1">Terjadi Kesalahan</h3>
                <p className="text-sm sm:text-base text-red-300 break-words">{error}</p>
              </div>
            </div>
          </div>
        )}



        {/* Generated Content */}
        <div className="space-y-4 sm:space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <h2 className="text-lg sm:text-xl font-semibold text-text-primary">Hasil Skripsi</h2>
            {Object.keys(generatedContent).length > 0 && (
              <div className="text-xs sm:text-sm text-text-tertiary">
                {Object.keys(generatedContent).length} / {outline.length} bagian selesai
              </div>
            )}
          </div>

          {outline.map((section, index) => {
            const content = generatedContent[section.id]
            const isExpanded = expandedSections.has(section.id)
            const isGenerated = !!content
            const isCurrent = !isCompleted && index === progress.current
            const isCompleted_section = index < progress.current
            const isDaftarPustaka = section.id === 'daftar_pustaka'

            return (
              <div key={section.id} className={cn(
                "bg-bg-secondary rounded-xl border overflow-hidden transition-all",
                isCurrent ? "border-blue-500/50 shadow-lg shadow-blue-500/10" : "border-border-primary",
                isCompleted_section && "border-emerald-500/30"
              )}>
                <div className="p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <div className="flex items-center space-x-2 sm:space-x-3 flex-1">
                      <div className={cn(
                        "w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium flex-shrink-0",
                        isCompleted_section ? "bg-emerald-500/20 text-emerald-400" :
                        isCurrent ? "bg-blue-500/20 text-blue-400" :
                        "bg-bg-tertiary text-text-tertiary"
                      )}>
                        {isCompleted_section ? (
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                        ) : isCurrent ? (
                          <Loader className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                        ) : (
                          index + 1
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg font-semibold text-text-primary break-words">{section.title}</h3>
                      </div>
                    </div>
                    
                    {(isGenerated || (isDaftarPustaka && isCompleted)) && (
                      <div className="flex-shrink-0">
                        <button
                          onClick={() => toggleSection(section.id)}
                          className={cn(buttonVariants.secondary, "text-sm flex items-center")}
                        >
                          {isExpanded ? 'Sembunyikan' : 'Tampilkan'}
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  {isGenerated && isExpanded && !isDaftarPustaka && (
                    <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-bg-primary rounded-lg border border-border-secondary">
                      <MarkdownContent content={content} />
                    </div>
                  )}
                  
                  {/* Daftar Pustaka Content */}
                  {isDaftarPustaka && isExpanded && isCompleted && (
                    <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-bg-primary rounded-lg border border-border-secondary">
                      <div className="space-y-4 sm:space-y-6">
                        <div className="prose prose-invert max-w-none text-text-secondary leading-relaxed">
                          <h4 className="text-base sm:text-lg font-semibold text-text-primary mb-3 sm:mb-4 flex items-center">
                            <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                            Daftar Pustaka ({allCitations.length})
                          </h4>
                          <p className="text-xs sm:text-sm text-text-tertiary mb-3 sm:mb-4">
                            Semua referensi yang digunakan dalam skripsi ini.
                          </p>
                          {allCitations.length > 0 ? (
                             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                               {allCitations.map((citation, index) => (
                                 <ReferenceCard key={index} reference={citation} index={index} />
                               ))}
                             </div>
                           ) : (
                             <p className="text-text-tertiary italic">Tidak ada referensi yang ditemukan.</p>
                           )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Loading State */}
                  {isCurrent && isLoading && (
                    <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-blue-500/5 rounded-lg border border-blue-500/20">
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <Loader className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 animate-spin" />
                        <span className="text-sm sm:text-base text-blue-400">Sedang menggenerate konten...</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Pending State */}
                  {!isGenerated && !isCurrent && !isDaftarPustaka && (
                    <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-bg-tertiary rounded-lg">
                      <span className="text-sm sm:text-base text-text-tertiary">Menunggu untuk diproses...</span>
                    </div>
                  )}
                  
                  {/* Daftar Pustaka Pending State */}
                  {isDaftarPustaka && !isCompleted && (
                    <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-bg-tertiary rounded-lg">
                      <span className="text-sm sm:text-base text-text-tertiary">Daftar pustaka akan tersedia setelah semua bagian selesai...</span>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

SkripsiResultPage.propTypes = {
  query: PropTypes.string.isRequired,
  outline: PropTypes.array.isRequired,
  generatedContent: PropTypes.object.isRequired,
  academicSources: PropTypes.array,
  progress: PropTypes.shape({
    current: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  isCompleted: PropTypes.bool.isRequired,
  onBack: PropTypes.func.isRequired,
  onBackToSearch: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  lastSaveTime: PropTypes.string
}

export default SkripsiResultPage